import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { Upload, CheckCircle2, FileText, AlertTriangle, Download, Trash2, Loader2, Eye, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { ComplianceResult } from "@/hooks/useComplianceLookup";
import { DocumentViewerModal } from "@/components/DocumentViewerModal";
import { useIsMobile } from "@/hooks/use-mobile";

interface DocumentoSoporte {
  id: string;
  codigo_partida: string;
  tipo_documento: string;
  descripcion_documento: string | null;
  entidad_emisora: string | null;
  notas: string | null;
  obligatorio: boolean;
}

interface ArchivoSubido {
  id: string;
  documento_soporte_id: string | null;
  archivo_url: string;
  nombre_archivo: string;
  descripcion_libre: string | null;
  created_at: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  codigoPartida: string;
  descripcionProducto: string;
  compliance?: ComplianceResult;
  documentoId?: string;
}

export function ComplianceModal({ open, onOpenChange, codigoPartida, descripcionProducto, compliance, documentoId }: Props) {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { toast } = useToast();
  const [docs, setDocs] = useState<DocumentoSoporte[]>([]);
  const [uploads, setUploads] = useState<Record<string, ArchivoSubido>>({});
  const [freeUploads, setFreeUploads] = useState<ArchivoSubido[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [uploadingFree, setUploadingFree] = useState(false);
  const [freeDocName, setFreeDocName] = useState("");
  const [viewerFile, setViewerFile] = useState<{ url: string; name: string } | null>(null);

  const cleanCode = codigoPartida.replace(/\D/g, "");
  const normalized = cleanCode.length < 10 ? cleanCode.padEnd(10, "0") : cleanCode;

  useEffect(() => {
    if (!open || !codigoPartida || !user) return;
    setLoading(true);
    const fetchData = async () => {
      const { data: docsData } = await supabase.from("documentos_soporte").select("*").eq("codigo_partida", normalized);
      const fetchedDocs = (docsData ?? []) as DocumentoSoporte[];
      setDocs(fetchedDocs);
      const { data: archivos } = await supabase.from("archivos_soporte_cargados").select("*").eq("codigo_partida", normalized).eq("user_id", user.id);
      const map: Record<string, ArchivoSubido> = {};
      const free: ArchivoSubido[] = [];
      (archivos ?? []).forEach((a: any) => {
        const archivo = a as ArchivoSubido;
        if (archivo.documento_soporte_id) { map[archivo.documento_soporte_id] = archivo; } else { free.push(archivo); }
      });
      setUploads(map);
      setFreeUploads(free);
      setLoading(false);
    };
    fetchData();
  }, [open, codigoPartida, user, normalized]);

  const handleUpload = async (doc: DocumentoSoporte, file: File) => {
    if (!user) return;
    setUploadingId(doc.id);
    try {
      const ext = file.name.split(".").pop() || "pdf";
      const filePath = `${user.id}/${normalized}/${doc.id}_${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("soportes_cumplimiento").upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = await supabase.storage.from("soportes_cumplimiento").createSignedUrl(filePath, 60 * 60 * 24 * 365);
      const archivoUrl = urlData?.signedUrl || filePath;
      const { data: inserted, error: insertError } = await supabase.from("archivos_soporte_cargados").insert({ user_id: user.id, documento_soporte_id: doc.id, documento_id: documentoId || null, codigo_partida: normalized, archivo_url: archivoUrl, nombre_archivo: file.name }).select().single();
      if (insertError) throw insertError;
      setUploads((prev) => ({ ...prev, [doc.id]: inserted as ArchivoSubido }));
      toast({ title: "✅ Archivo cargado exitosamente" });
    } catch (err: any) {
      toast({ title: "Error al subir archivo", description: err.message, variant: "destructive" });
    } finally { setUploadingId(null); }
  };

  const handleDelete = async (doc: DocumentoSoporte) => {
    const archivo = uploads[doc.id];
    if (!archivo) return;
    try {
      await supabase.from("archivos_soporte_cargados").delete().eq("id", archivo.id);
      setUploads((prev) => { const next = { ...prev }; delete next[doc.id]; return next; });
      toast({ title: "🗑️ Archivo eliminado" });
    } catch (err: any) { toast({ title: "Error al eliminar", description: err.message, variant: "destructive" }); }
  };

  const handleFreeUpload = async (file: File) => {
    if (!user) return;
    const label = freeDocName.trim() || file.name;
    setUploadingFree(true);
    try {
      const ext = file.name.split(".").pop() || "pdf";
      const filePath = `${user.id}/${normalized}/adicional_${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("soportes_cumplimiento").upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = await supabase.storage.from("soportes_cumplimiento").createSignedUrl(filePath, 60 * 60 * 24 * 365);
      const archivoUrl = urlData?.signedUrl || filePath;
      const { data: inserted, error: insertError } = await supabase.from("archivos_soporte_cargados").insert({ user_id: user.id, documento_soporte_id: null, documento_id: documentoId || null, codigo_partida: normalized, archivo_url: archivoUrl, nombre_archivo: file.name, descripcion_libre: label } as any).select().single();
      if (insertError) throw insertError;
      setFreeUploads((prev) => [...prev, inserted as ArchivoSubido]);
      setFreeDocName("");
      toast({ title: "✅ Documento adicional cargado" });
    } catch (err: any) { toast({ title: "Error al subir archivo", description: err.message, variant: "destructive" }); }
    finally { setUploadingFree(false); }
  };

  const handleDeleteFree = async (archivo: ArchivoSubido) => {
    try {
      await supabase.from("archivos_soporte_cargados").delete().eq("id", archivo.id);
      setFreeUploads((prev) => prev.filter((a) => a.id !== archivo.id));
      toast({ title: "🗑️ Documento eliminado" });
    } catch (err: any) { toast({ title: "Error al eliminar", description: err.message, variant: "destructive" }); }
  };

  const regla = compliance?.regla;
  const riesgo = (regla?.nivel_riesgo ?? "").toLowerCase();
  const isCritico = riesgo === "crítico" || riesgo === "critico";
  const isAltoMedio = riesgo === "alto" || riesgo === "medio";

  const content = (
    <div className="space-y-4">
      {/* Compliance summary */}
      <Card className="p-4 space-y-2 bg-muted/30 border-border">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground">Nivel de Riesgo:</span>
          <Badge className={cn("text-[10px]", isCritico && "bg-destructive text-destructive-foreground", isAltoMedio && "bg-orange-500 text-white", !isCritico && !isAltoMedio && "bg-muted text-muted-foreground")}>
            {regla?.nivel_riesgo || "—"}
          </Badge>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="font-semibold text-muted-foreground">Entidad:</span> {regla?.entidad}</div>
          <div><span className="font-semibold text-muted-foreground">Trámite:</span> {regla?.tipo_tramite}</div>
        </div>
        <p className="text-xs text-muted-foreground">{regla?.descripcion_regla}</p>
        {regla?.norma && <p className="text-xs text-muted-foreground"><span className="font-semibold">Norma:</span> {regla.norma}</p>}
      </Card>

      {/* Required documents */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <FileText className="h-4 w-4" /> Documentos Soporte Requeridos
        </h4>
        {loading && <p className="text-xs text-muted-foreground py-4 text-center">Cargando...</p>}
        {!loading && docs.length === 0 && (
          <p className="text-xs text-muted-foreground py-6 text-center border border-dashed border-border rounded-lg">
            No hay documentos soporte configurados para esta partida arancelaria.
          </p>
        )}
        {docs.map((doc) => {
          const archivo = uploads[doc.id];
          const isUploading = uploadingId === doc.id;
          return (
            <Card key={doc.id} className={cn("p-3 border-border", archivo && "border-green-500/50 bg-green-500/5")}>
              <div className={cn("flex gap-3", isMobile ? "flex-col" : "items-start justify-between")}>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground">{doc.tipo_documento}</span>
                    {doc.obligatorio && !archivo && <Badge className="bg-destructive text-destructive-foreground text-[9px] px-1.5 py-0">Requerido</Badge>}
                    {archivo && <Badge className="bg-green-600 text-white text-[9px] px-1.5 py-0 flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" /> Cargado</Badge>}
                  </div>
                  {doc.descripcion_documento && <p className="text-xs text-muted-foreground">{doc.descripcion_documento}</p>}
                  {doc.entidad_emisora && <p className="text-xs text-muted-foreground"><span className="font-semibold">Emisor:</span> {doc.entidad_emisora}</p>}
                  {doc.notas && <p className="text-xs text-muted-foreground italic">{doc.notas}</p>}
                  {archivo && <p className="text-[10px] text-muted-foreground/70 mt-1">📎 {archivo.nombre_archivo}</p>}
                </div>
                <div className={cn("shrink-0 flex items-center gap-1", isMobile && "justify-end")}>
                  {isUploading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  ) : archivo ? (
                    <>
                      <Button variant="ghost" size="icon" className={cn("text-muted-foreground hover:text-primary", isMobile ? "h-10 w-10" : "h-8 w-8")} onClick={() => setViewerFile({ url: archivo.archivo_url, name: archivo.nombre_archivo })}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <a href={archivo.archivo_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className={cn("text-primary hover:text-primary/80", isMobile ? "h-10 w-10" : "h-8 w-8")}><Download className="h-4 w-4" /></Button>
                      </a>
                      <Button variant="ghost" size="icon" className={cn("text-muted-foreground hover:text-destructive", isMobile ? "h-10 w-10" : "h-8 w-8")} onClick={() => handleDelete(doc)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <label className="cursor-pointer">
                      <input type="file" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleUpload(doc, file); e.target.value = ""; }} />
                      <Button variant="outline" size={isMobile ? "default" : "sm"} className={cn("text-xs gap-1.5 pointer-events-none", isMobile && "h-11 w-full")}>
                        <Upload className="h-3.5 w-3.5" /> Subir Archivo
                      </Button>
                    </label>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Free uploads */}
      <Separator />
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Documentos Adicionales (Opcional)
        </h4>
        <div className={cn("flex gap-2", isMobile ? "flex-col" : "items-center")}>
          <Input value={freeDocName} onChange={(e) => setFreeDocName(e.target.value)} placeholder="Ej: Ficha técnica, Carta aclaratoria..." className={cn("text-xs flex-1", isMobile ? "h-11" : "h-9")} />
          <label className={cn("cursor-pointer shrink-0", isMobile && "w-full")}>
            <input type="file" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFreeUpload(file); e.target.value = ""; }} />
            <Button variant="outline" size={isMobile ? "default" : "sm"} className={cn("text-xs gap-1.5 pointer-events-none", isMobile && "h-11 w-full")} disabled={uploadingFree}>
              {uploadingFree ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Subir
            </Button>
          </label>
        </div>
        {freeUploads.map((archivo) => (
          <Card key={archivo.id} className="p-3 border-border border-green-500/50 bg-green-500/5">
            <div className={cn("flex gap-3", isMobile ? "flex-col" : "items-start justify-between")}>
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">{archivo.descripcion_libre || archivo.nombre_archivo}</span>
                  <Badge className="bg-secondary text-secondary-foreground text-[9px] px-1.5 py-0">Adicional</Badge>
                </div>
                <p className="text-[10px] text-muted-foreground/70">📎 {archivo.nombre_archivo}</p>
              </div>
              <div className={cn("shrink-0 flex items-center gap-1", isMobile && "justify-end")}>
                <Button variant="ghost" size="icon" className={cn("text-muted-foreground hover:text-primary", isMobile ? "h-10 w-10" : "h-8 w-8")} onClick={() => setViewerFile({ url: archivo.archivo_url, name: archivo.descripcion_libre || archivo.nombre_archivo })}>
                  <Eye className="h-4 w-4" />
                </Button>
                <a href={archivo.archivo_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="icon" className={cn("text-primary hover:text-primary/80", isMobile ? "h-10 w-10" : "h-8 w-8")}><Download className="h-4 w-4" /></Button>
                </a>
                <Button variant="ghost" size="icon" className={cn("text-muted-foreground hover:text-destructive", isMobile ? "h-10 w-10" : "h-8 w-8")} onClick={() => handleDeleteFree(archivo)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {freeUploads.length === 0 && !uploadingFree && (
          <p className="text-[11px] text-muted-foreground text-center py-2">No hay documentos adicionales cargados.</p>
        )}
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? (
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="max-h-[92vh]">
            <DrawerHeader>
              <DrawerTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className={cn("h-5 w-5", isCritico && "text-destructive", isAltoMedio && "text-orange-500")} />
                Cumplimiento Normativo
              </DrawerTitle>
              <DrawerDescription className="text-xs">{descripcionProducto}</DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-6 overflow-y-auto">
              {content}
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className={cn("h-5 w-5", isCritico && "text-destructive", isAltoMedio && "text-orange-500")} />
                Cumplimiento Normativo
              </DialogTitle>
              <DialogDescription className="text-xs">{descripcionProducto}</DialogDescription>
            </DialogHeader>
            {content}
          </DialogContent>
        </Dialog>
      )}

      <DocumentViewerModal
        open={!!viewerFile}
        onOpenChange={(o) => { if (!o) setViewerFile(null); }}
        url={viewerFile?.url || ""}
        title={viewerFile?.name}
      />
    </>
  );
}
